import { useEffect } from "react";
import "../styles/Contents.css";

export default function Class10() {
  useEffect(() => {
    const elements = document.querySelectorAll(
      ".reveal, .reveal-left, .reveal-right"
    );

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          } else {
            entry.target.classList.remove("active");
          }
        });
      },
      { threshold: 0.15 }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="page">
      {/* HERO */}
      <section className="about-hero reveal">
        <h1>Class 10 Stay & Study Program</h1>
        <p>
          A focused residential learning program designed to help Class 10
          students master concepts, build exam confidence, and achieve academic
          excellence in board examinations.
        </p>
      </section>

      {/* STUDY STRATEGY FRAMEWORK */}
      <section className="mission-section reveal">
        <h2>📖 How We Help Students Score High in Class 10</h2>

        <div className="mission-grid">
          <div className="mission-card">
            <h3>🗓️ Prepare a Study Schedule</h3>
            <p>
              We divide the entire syllabus into daily, weekly, and monthly plans so
              students complete preparation well before exams without last-minute stress.
            </p>
          </div>

          <div className="mission-card">
            <h3>⏰ Identify Best Study Hours</h3>
            <p>
              Students follow a timetable aligned with their peak concentration hours,
              ensuring maximum focus and effective learning every day.
            </p>
          </div>

          <div className="mission-card">
            <h3>🎯 Prioritize Important Topics</h3>
            <p>
              High-weightage and frequently asked topics are given priority to maximize
              scoring potential in board examinations.
            </p>
          </div>

          <div className="mission-card">
            <h3>🔁 Daily Revision</h3>
            <p>
              Every day ends with structured revision through writing practice,
              formula recall, and concept reinforcement.
            </p>
          </div>

          <div className="mission-card">
            <h3>📝 Sunday – Test Day</h3>
            <p>
              Weekly subject-wise and topic-wise tests are conducted every Sunday to
              evaluate progress and build exam confidence.
            </p>
          </div>

          <div className="mission-card">
            <h3>💪 Consistency & Discipline</h3>
            <p>
              A disciplined residential routine ensures students stay consistent,
              motivated, and focused throughout the academic year.
            </p>
          </div>
        </div>
      </section>


      {/* SECTION 1 */}
      <section className="about-section reveal-left">
        <h1>How we Guide the Students </h1>
        <div className="about-row">

          <div className="about-content">
            <h2>📘 Understand Your Syllabus & Exam Pattern</h2>
            <p>
              Before starting preparation, students must clearly understand what
              the board expects from them. Our Stay & Study program ensures this
              clarity from day one.
            </p>
            <ul>
              <li>Detailed subject-wise chapter and topic coverage</li>
              <li>Clear explanation of marking schemes and weightage</li>
              <li>Regular exposure to model and sample question papers</li>
              <li>Understanding common question types and difficulty levels</li>
            </ul>
          </div>
          <img
            src="/images/class10-1.jpg"
            alt="Syllabus and Exam Pattern"
            className="about-image"
          />
        </div>
      </section>

      {/* SECTION 2 */}
      <section className="about-section reveal-right">
        <div className="about-row reverse">
          <div className="about-content">
            <h2>🗓️ Structured Study Schedule</h2>
            <p>
              Consistency is the key to success in Class 10. Our residential
              environment follows a well-planned academic schedule.
            </p>
            <ul>
              <li>Balanced time allocation for all subjects</li>
              <li>Extra focus on difficult subjects and weak areas</li>
              <li>Daily, weekly, and monthly academic goals</li>
              <li>Planned revision slots for long-term retention</li>
              <li>Short breaks to avoid burnout and improve focus</li>
            </ul>
          </div>

          <img
            src="/images/class10-2.jpg"
            alt="Study Schedule"
            className="about-image"
          />
        </div>
      </section>

      {/* SECTION 3 */}
      <section className="about-section reveal-left">
        <div className="about-row">
          <div className="about-content">
            <h2>🧠 Focus on Conceptual Understanding</h2>
            <p>
              Memorization alone is not enough. Our Stay & Study program focuses
              on building strong fundamentals and deep conceptual clarity.
            </p>
            <ul>
              <li>Active learning through explanations and discussions</li>
              <li>Use of diagrams, mind maps, and real-life examples</li>
              <li>Immediate doubt clarification by faculty</li>
              <li>Extensive practice for Mathematics and Science subjects</li>
              <li>Encouraging students to explain concepts in their own words</li>
            </ul>
          </div>
          <img
            src="/images/class10-3.jpg"
            alt="Conceptual Learning"
            className="about-image"
          />
        </div>
      </section>

      {/* SECTION 4 */}
      <section className="about-section reveal-right">
        <div className="about-row reverse">
          <div className="about-content">
            <h2>📝 Practice with Previous Year Papers & Mock Tests</h2>
            <p>
              Regular testing is essential to prepare students for real board
              exam conditions and boost confidence.
            </p>
            <ul>
              <li>Previous year question paper practice</li>
              <li>Frequent mock tests under exam-like conditions</li>
              <li>Training in time management and answer presentation</li>
              <li>Identification of weak areas through test analysis</li>
              <li>Reduced exam fear through repeated exam exposure</li>
            </ul>
          </div>

          <img
            src="/images/class10-4.jpg"
            alt="Mock Tests"
            className="about-image"
          />
        </div>
      </section>

      {/* SECTION 5 */}
      <section className="about-section reveal-left">
        <div className="about-row">
          <div className="about-content">
            <h2>💙 Student Well-being & Balanced Lifestyle</h2>
            <p>
              Academic success goes hand in hand with physical and mental
              well-being. Our Stay & Study program ensures a healthy balance.
            </p>
            <ul>
              <li>7–8 hours of adequate sleep every day</li>
              <li>Nutritious and hygienic meals for sustained energy</li>
              <li>Short breaks and light physical activities</li>
              <li>Stress management and motivational support</li>
              <li>Positive, disciplined, and supportive environment</li>
            </ul>
          </div>
          <img
            src="/images/class10-5.jpg"
            alt="Student Well Being"
            className="about-image"
          />
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="about-section reveal-right">
        <div className="about-row reverse">
          <div className="about-content">
            <h2>⭐ Why Choose ACCESS Stay & Study for Class 10?</h2>
            <p>
              Our Stay & Study program is designed to provide students with the
              perfect balance of academic discipline, personal care, and exam-focused
              preparation in a safe residential environment.
            </p>
            <ul>
              <li>Experienced and dedicated faculty for all core subjects</li>
              <li>24/7 monitored, distraction-free residential campus</li>
              <li>Daily supervised study hours with mentor support</li>
              <li>Regular tests, revisions, and detailed performance tracking</li>
              <li>Individual attention for every student</li>
              <li>Strong focus on board exam results and concept mastery</li>
              <li>Safe hostel facilities with nutritious food</li>
              <li>Emphasis on discipline, confidence, and self-growth</li>
            </ul>
          </div>

          <img
          src="/images/hostel2.jpeg"
          alt="Why Choose ACCESS"
          className="about-image"
          />
        </div>
      </section>


      {/* CLASS 10 FEES CARD */}
      <section className="about-section reveal">
        <div className="fees-card-wrapper">
            <div className="fees-card">
                <span className="fees-badge">Class 10</span>

                <h2 className="fees-title">Stay & Study Program</h2>

                <div className="fees-amount">
                    <span className="currency">₹</span>85,000
                </div>

                <p className="fees-duration">Per Academic Year</p>

                <ul className="fees-details">
                    <li>✔ Fully residential hostel accommodation</li>
                    <li>✔ Nutritious daily meals</li>
                    <li>✔ Structured academic schedule</li>
                    <li>✔ Dedicated study halls & classrooms</li>
                    <li>✔ Faculty mentoring & doubt clearing</li>
                    <li>✔ Regular tests & performance analysis</li>
                </ul>

                <a
                 href={`https://wa.me/919964585585?text=Hello ACCESS Team, I would like to enquire.`}
                 target="_blank"
                 rel="noreferrer"
                >
                <button className="fees-cta whatsapp-btn">
                <span className="wa-icon">💬</span>
                Enquire Now
                </button>
                </a>
            </div>
          </div>
        </section>


      {/* FOOTER */}
      <section className="about-footer reveal">
        <p>
          The Class 10 Stay & Study Program at ACCESS provides a disciplined
          residential environment where students learn effectively, practice
          consistently, and grow confidently towards outstanding board exam
          results.
        </p>
      </section>
    </div>
  );
}
